import { useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router';
import { UserContext } from '~/app';
import { HlxProviderTermsGate } from '~/features/terms/HlxProviderTermsGate';
import { authDebug, summarizeUrl } from './authDebug';
import { resolvePostAuthDestination } from './postAuthRedirect';
import { isProviderAllowedRoute, normalizeRoutePathname } from './providerAllowedRoutes';
import { paths } from './routes';

/**
 * Protected HLX Route
 *
 * Allows HLX access for authenticated employer and impersonation states, plus
 * provider-facing HLX pages that remain linkable from the vault. Those provider
 * pages sit behind the Platform Participation Agreement gate.
 */
export const ProtectedHLXRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isAuthSettling = UserContext.useSelector(
    (state) =>
      state.matches('$_ANONYMOUS') ||
      state.matches('$_AUTHENTICATING') ||
      state.matches('$_FETCHING_USER') ||
      state.matches('$_DETERMINE_ROLE')
  );
  const isRegistration = UserContext.useSelector(
    (state) =>
      state.matches('$_CHOOSE_ROLE') ||
      state.matches('$_PROVIDER_REGISTRATION') ||
      state.matches('$_EMPLOYER_REGISTRATION')
  );
  const isProvider = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const isImpersonating = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const isHlxAuthorized = UserContext.useSelector(
    (state) =>
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_EMPLOYER' }) ||
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_ADMIN' }) ||
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );

  if (isAuthSettling) {
    authDebug('protected-hlx.auth_settling', {
      pathname: location.pathname,
    });
    return <div>Loading...</div>;
  }

  if (isRegistration) {
    authDebug('protected-hlx.registration_redirect', {
      pathname: location.pathname,
      target: paths.register.roleSelect,
    });
    return <Navigate to={paths.register.roleSelect} replace />;
  }

  const normalizedPathname = normalizeRoutePathname(location.pathname);
  const isAllowedProviderPath = isProviderAllowedRoute(normalizedPathname);

  if (isImpersonating && normalizedPathname === paths.hlx.settings) {
    return <Navigate to={paths.landing} replace />;
  }

  if (isProvider && isAllowedProviderPath) {
    // [MCV-1815] A provider accepts the Platform Participation Agreement once before
    // using HLX. Employers, admins and impersonated sessions are not gated.
    return <HlxProviderTermsGate>{children}</HlxProviderTermsGate>;
  }

  if (isProvider) {
    return (
      <ProviderHlxRedirect
        requestedPath={`${location.pathname}${location.search}${location.hash}`}
      />
    );
  }

  if (!isHlxAuthorized) {
    authDebug('protected-hlx.unauthorized_redirect_to_sign_in', {
      pathname: location.pathname,
      target: paths.publicAuth.signIn,
    });
    return <Navigate to={paths.publicAuth.signIn} replace />;
  }

  return <>{children}</>;
};

const ProviderHlxRedirect = ({ requestedPath }: { requestedPath: string }) => {
  const destination = useMemo(
    () =>
      resolvePostAuthDestination({
        role: 'provider',
        hostname: window.location.hostname,
        returnTo: requestedPath,
      }),
    [requestedPath]
  );

  useEffect(() => {
    if (destination.type !== 'external') return;

    authDebug('protected-hlx.provider_external_redirect', {
      requestedPath,
      target: summarizeUrl(destination.url),
    });
    window.location.assign(destination.url);
  }, [destination, requestedPath]);

  if (destination.type === 'local') {
    authDebug('protected-hlx.provider_local_redirect', {
      requestedPath,
      target: destination.path,
    });
    return <Navigate to={destination.path} replace />;
  }

  return null;
};
